import { Button, Grid, IconButton, makeStyles, TextField, Theme, Typography } from "@material-ui/core";
import React, { useMemo, useState } from "react";
import { LibraryItem } from "../../../entities/LibraryItem";
import { LibraryItemType } from "../../../entities/LibraryItemType";
import { FieldValidationError, ValidationHelper } from "../../../services/server/ServerResponses";
import DateHelper from "../../../utilities/DateHelper";

import EditIcon from "@material-ui/icons/Edit";
import StarIcon from "@material-ui/icons/Star";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import { FolderService } from "../../../services/FolderService";
import { useLibrary, useLibraryAlert } from "../LibraryPage";
import { convertFolderToLibraryItem } from "../../../entities/Folder";
import { DrawerHeader } from "./Components/DrawerHeader";
import { DetailRow } from "./Components/DetailRow";
import { GridGrow } from "../../../components/GridGrow";
import { SermonSeriesService } from "../../../services/SermonSeriesService";
import { convertSeriesToLibraryItem } from "../../../entities/SermonSeries";


const useStyles = makeStyles((theme: Theme) => ({
    infoContainer: {
        fontFamily: "Roboto, Helvetica",
        fontSize: "13px",
        color: "#202124",
    },
    infoRow: {
        fontFamily: "Roboto, Helvetica",
        fontSize: 13,
        color: "#202124", 
        paddingTop: 6, 
        paddingBottom: 6,
    },
    inlineIcon: {
        color: "rgb(143, 143, 143)",
        fontSize: 16,
        verticalAlign: "text-bottom",
        float: "right",
        marginRight: 6,
        cursor: "pointer",
    },
    infoValue: {
        boxSizing: "border-box",
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontWeight: 500,
        cursor: "pointer",
        color: "#1967d2",
    },
    editInput: {
        fontSize: "12px",
        width: "244px",
        marginBottom: "4px",
        "&:first-of-type": {
            marginBottom: "12px",
        },
    },
}));


interface Props {
    item:LibraryItem;
}

const getFolderTypeString = (type:LibraryItemType) => {
    switch(type){
        case LibraryItemType.PremiumSermon: return "Premium Sermon";
        case LibraryItemType.PremiumSermonSeries: return "Premium Series";
        default: return "Folder"
    }
}

export function FolderView(props:Props){
    const { item } = props;
    const classes = useStyles();
    const [newTitle, setNewTitle] = useState(item.name);
    const [editMode, setEditMode] = useState(false);
    const [disable, setDisable] = useState(false);
    const [errors, setErrors] = useState<FieldValidationError[]>([]);
    const libraryAlert = useLibraryAlert();
    const context = useLibrary();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useMemo(() => setEditMode(false), [item])

    const handleSave = async() => {
        setErrors([]);
        if(newTitle.trim().length === 0)
            return setErrors([{field: "Name", errors:["Name cannot be blank"]}]);

        setDisable(true);
        const response = item.type === LibraryItemType.SermonSeries 
            ? await SermonSeriesService.setNameSeries(item.id, newTitle)
            : await FolderService.setFolderName(item.id, newTitle);
        setDisable(false);
    
        if(response.error) return libraryAlert(response.message);
        if(response.validation) return setErrors(response.errors);

		context.updateLibraryItem(convertFolderToLibraryItem(response.data));
		libraryAlert(`${item.name} renamed to ${newTitle}`, async () => {
            const result = item.type === LibraryItemType.SermonSeries 
                ? await SermonSeriesService.setNameSeries(item.id, item.name)
                : await FolderService.setFolderName(item.id, item.name);
			if(result.success) {
                const updatedItem = item.type === LibraryItemType.SermonSeries
                    ? convertSeriesToLibraryItem(response.data)
                    : convertFolderToLibraryItem(response.data)
				context.updateLibraryItem(updatedItem);
				libraryAlert(`${newTitle} renamed to ${item.name}`);
			} else {
				libraryAlert(`Failed to rename ${newTitle}`);
			}
		});
        setEditMode(false)
    }

    const toggleStar = async() => {
        setErrors([]);

        setDisable(true);
        const response = item.type === LibraryItemType.SermonSeries 
            ? await SermonSeriesService.setStarredSeries(item.id, !item.isStarred)
            : await FolderService.setStarredFolder(item.id, !item.isStarred);
        setDisable(false);
    
        if(response.error) return libraryAlert(response.message);
        if(response.success){
            const updatedItem = item.type === LibraryItemType.SermonSeries
                ? convertSeriesToLibraryItem(response.data)
                : convertFolderToLibraryItem(response.data)
            context.updateLibraryItem(updatedItem);
            libraryAlert(`${updatedItem.isStarred ? "Star added to" : "Star removed from"} ${item.name}`)
        }
    }

    return (
        <>
            <DrawerHeader item={item} />
            <div>
                <Grid container alignItems="center">
                    <Grid item>
                        <Typography variant="subtitle1">
                            {editMode ? "Edit Details" : "Details"}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <IconButton onClick={() => setEditMode(!editMode)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Grid>
                </Grid>
                <div className={classes.infoContainer}>
                    {editMode
                        ?
                        <TextField
                            error={ValidationHelper.isFieldInError(errors, "Name")}
                            placeholder="Title"
                            className={classes.editInput}
                            value={newTitle}
                            onChange={(e) => setNewTitle(e.currentTarget.value)}
                            inputProps={{
                                style: { fontSize: 12 },
                                "aria-label": "Name",
                            }}
                            helperText={ValidationHelper.getFieldErrorSummary(errors, "Name")}
                            disabled={disable}
                        />
                        :
                        <Grid container alignItems="center" className={classes.infoRow}>
                            <Grid item xs={5} container alignItems="center" style={{color: "#5f6368"}}>
                                <Grid item>
                                    Title
                                </Grid>
                                <GridGrow />
                                <Grid item onClick={() => toggleStar()}>
                                    {item.isStarred 
                                    ? <StarIcon className={classes.inlineIcon} />
                                    : <StarBorderIcon className={classes.inlineIcon} />
                                    }
                                </Grid>
                            </Grid>
                            <Grid item xs={7}
                                className={classes.infoValue}
                                onClick={() => setEditMode(true)}
                            >
                                {item.name}
                            </Grid>
                        </Grid>
                    }
                    <DetailRow 
                        property="Type" 
                        value={getFolderTypeString(item.type)}
                    />
                    <DetailRow 
                        property="Modified" 
                        value={DateHelper.buildDateFormat(item.dateModified)}
                    />
                    <DetailRow 
                        property="Access" 
                        value={DateHelper.buildDateFormat(item.dateAccessed)}
                    />
                    <DetailRow 
                        property="Created" 
                        value={DateHelper.buildDateFormat(item.dateCreated)}
                    />
                    {editMode && (
                    <div style={{textAlign: "right", paddingRight: 16,}}>
                        <Button onClick={() => setEditMode(false)} color="primary" disabled={disable}>
                            Cancel
                        </Button>
                        <Button onClick={() => handleSave()} color="primary" disabled={disable}>
                            Save
                        </Button>
                    </div>
                    )}
                </div>
            </div>
        </>
    );
}